import { Box } from '@mui/material';
import {
  type FunctionComponent,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

/**
 * Renders `label` and, if it overflows the line cap, swaps in a middle-
 * truncated form: first few words + " ... " + as many trailing words as
 * fit. A hidden sibling carries the same computed font/whitespace styling
 * as the visible node so width/wrap behavior matches exactly. The visible
 * element's textContent is never touched, so search-highlight spans
 * survive ResizeObserver fires.
 */
const MAX_HEAD_WORDS = 4;
const DEFAULT_LINE_HEIGHT_PX = 20;
const DEFAULT_MAX_LINES = 2;

const SYNCED_STYLE_PROPS = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'wordSpacing',
  'lineHeight',
  'whiteSpace',
  'wordBreak',
  'overflowWrap'
] as const;

const buildTruncated = (words: string[], tailStart: number): string => {
  const head = words.slice(0, MAX_HEAD_WORDS).join(' ');
  return tailStart < words.length
    ? `${head} ... ${words.slice(tailStart).join(' ')}`
    : `${head} ...`;
};

type MiddleTruncatedLabelProps = {
  label: string;
  maxLines?: number;
  /**
   * Pixel line height used to size the line box and compute the multi-line
   * overflow threshold. Set this when wrapping non-default typography (e.g.
   * page headers) so the line box matches the parent font size.
   */
  lineHeightPx?: number;
  /**
   * Optional render-pass over the visible text (e.g. to inject search-term
   * highlight marks). Receives the post-truncation text.
   */
  renderText?: (text: string) => ReactNode;
};

export const MiddleTruncatedLabel: FunctionComponent<
  MiddleTruncatedLabelProps
> = ({
  label,
  maxLines = DEFAULT_MAX_LINES,
  lineHeightPx = DEFAULT_LINE_HEIGHT_PX,
  renderText
}) => {
  const words = useMemo(() => label.trim().split(/\s+/), [label]);
  const isTruncatable = words.length > MAX_HEAD_WORDS;

  const visibleRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [tailStart, setTailStart] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!isTruncatable) {
      setTailStart(null);
      return;
    }
    const vis = visibleRef.current;
    const mea = measureRef.current;
    if (!vis || !mea) {
      return;
    }

    const compute = () => {
      const width = vis.clientWidth;
      if (width <= 0) {
        return;
      }
      const computed = getComputedStyle(vis);
      for (const prop of SYNCED_STYLE_PROPS) {
        mea.style[prop] = computed[prop];
      }
      mea.style.width = `${width}px`;

      const overflows =
        maxLines === 1
          ? () => mea.scrollWidth - 1 > width
          : () => mea.scrollHeight - 1 > lineHeightPx * maxLines;

      mea.textContent = label;
      if (!overflows()) {
        setTailStart(null);
        return;
      }
      let lo = MAX_HEAD_WORDS;
      let hi = words.length;
      let result = words.length;
      while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        mea.textContent = buildTruncated(words, mid);
        if (!overflows()) {
          result = mid;
          hi = mid;
        } else {
          lo = mid + 1;
        }
      }
      setTailStart(result);
    };

    compute();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(compute);
    observer.observe(vis);
    return () => observer.disconnect();
  }, [isTruncatable, label, words, maxLines, lineHeightPx]);

  const visibleText =
    tailStart != null ? buildTruncated(words, tailStart) : label;

  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <Box
        ref={visibleRef}
        sx={{
          overflow: 'hidden',
          wordBreak: 'break-word',
          whiteSpace: maxLines === 1 ? 'nowrap' : 'normal',
          lineHeight: `${lineHeightPx}px`,
          maxHeight: `${lineHeightPx * maxLines}px`,
          minWidth: 0
        }}
      >
        {renderText ? renderText(visibleText) : visibleText}
      </Box>
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -1
        }}
      />
    </Box>
  );
};
